import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { LAYERS } from 'bloko/blocks/drop/constants';
import { instanceOfDOMNodeOrNull } from 'bloko/common/helpers/customPropTypes';
import { getThemeClass } from 'bloko/blocks/drop/common';
import Icon, { IconLink } from 'bloko/blocks/icon';
import DropBase from 'bloko/blocks/drop/Base';
import defaults, { INFO_PLACEMENTS, THEMES, BASE_CLASS_NAME, BEHAVIOR } from './constants';

import './info.less';

const ICON_MAPPER = {
    [THEMES.light]: Icon.kinds.default,
    [THEMES.dark]: Icon.kinds.inverted,
    [THEMES.bright]: Icon.kinds.inverted,
    [THEMES.info]: Icon.kinds.inverted,
};

export default function Info({ theme, showCloseButton, render, onClose, ...props }) {
    const classNames = useMemo(() => [BASE_CLASS_NAME, getThemeClass(theme)], [theme]);

    const renderFunc = useCallback(
        () => {
            if (!showCloseButton) {
                return render();
            }

            return (
                <div className="bloko-drop__content-wrapper">
                    <div className="bloko-drop__content">{render()}</div>
                    <div className="bloko-drop__close">
                        <IconLink onClick={onClose} data-qa="bloko-drop-info-close-button">
                            <Icon initial={ICON_MAPPER[theme]} view={Icon.views.cancel} />
                        </IconLink>
                    </div>
                </div>
            );
        },
        [showCloseButton, render, theme, onClose]
    );

    return (
        <DropBase
            {...props}
            onClose={onClose}
            onlySetPlacement={false}
            behavior={BEHAVIOR}
            baseClassNames={classNames}
            render={renderFunc}
        />
    );
}

Info.layers = LAYERS;
Info.placements = INFO_PLACEMENTS;
Info.themes = THEMES;
Info.defaultProps = {
    ...defaults,
    show: false,
    host: null,
};
Info.propTypes = {
    /** Предпочтительное положение info, доступны в статическом свойстве [placements](#info-placements). */
    placement: PropTypes.oneOf(Object.values(INFO_PLACEMENTS)),
    /** Class z-index-а info, доступны в статическом свойстве [layers](#info-layers) */
    layer: PropTypes.oneOf(Object.values(LAYERS)),
    /** Возможные темы info доступны в статическом свойстве [themes](#info-themes)*/
    theme: PropTypes.oneOf(Object.values(Info.themes)),
    /** DOM нода хоста в рамках которого нужно рендерить info, по дефолту рендер будет в родителе компонента.*/
    host: instanceOfDOMNodeOrNull(),
    /** Флаг отвечает за показ info, `true` - показать, `false` - скрыть */
    show: PropTypes.bool,
    /** Элемент инициатор, относительно которого показывать всплывающую подсказку */
    children: PropTypes.node,
    /** Метод-рендер контента info */
    render: PropTypes.func.isRequired,
    /** Колбек вызываемый при закрытии компонента. Срабатывает в случае клика на крестик или клика вне компонента */
    onClose: PropTypes.func,
    /** Флаг — рисовать ли крестик */
    showCloseButton: PropTypes.bool,
    /** Флаг — закрывать ли info по клику извне */
    closeByClickOutside: PropTypes.bool,
    /** Метка, использовать ли тянущийся info.
     * Тянущийся info будет растягивать по контенту, ограничиваясь только размерами экрана */
    flexible: PropTypes.bool,
    /** dataQa='bloko-drop-info' Data-qa активного info */
    dataQa: PropTypes.string,
};
